import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowDown, History, LoaderCircle } from 'lucide-react';
import { buildChatTimeline, type TimelineEntry } from '@/lib/chatTimeline';
import type { ContactSession, ConversationChannel, TranscriptMessage } from '@/services/conversationService';
import MessageBubble from './MessageBubble';
import { useServerNow } from './useServerNow';

/** Within this distance of the end, new messages keep the view pinned to the bottom. */
const NEAR_BOTTOM_PX = 96;
/** Scrolled this close to the top, the next older page loads by itself. */
const LOAD_OLDER_PX = 160;
/** The live region stays silent this long after older history went in front. */
const QUIET_AFTER_PREPEND_MS = 400;

export interface SessionJump {
  conversationId: number;
  /** Changes for every request, so the same session can be jumped to twice. */
  nonce: number;
}

type Props = {
  messages: TranscriptMessage[];
  sessions: ContactSession[];
  channel: ConversationChannel;
  me: string | null;
  hasMore?: boolean;
  loadingOlder?: boolean;
  onLoadOlder?: () => void;
  /** "Agent schreibt …" under the last message. */
  agentTyping?: boolean;
  /** A colleague types in the web app. */
  staffTyping?: string | null;
  jump?: SessionJump | null;
  emptyText?: string;
  /** Extra rows after the messages that are not messages (e.g. a live call's partial line). */
  footer?: ReactNode;
};

function SessionSeparator({ entry }: { entry: Extract<TimelineEntry, { type: 'session' }> }) {
  return (
    <div className="flex items-center gap-3 pb-1 pt-5 first:pt-1" data-session={entry.conversationId} role="separator">
      <span className="h-px flex-1 bg-border" aria-hidden="true" />
      <span className="flex items-center gap-1.5 rounded-full border border-border/80 bg-card px-3 py-1 text-[11.5px] font-medium text-muted-foreground shadow-sm">
        <span className="text-foreground">{entry.label}</span>
        {entry.outcome && (
          <>
            <span aria-hidden="true">·</span>
            <span>{entry.outcome}</span>
          </>
        )}
        {entry.isTest && (
          <span className="rounded bg-muted px-1 text-[10px] font-semibold uppercase tracking-wide">Test</span>
        )}
      </span>
      <span className="h-px flex-1 bg-border" aria-hidden="true" />
    </div>
  );
}

function DaySeparator({ label }: { label: string }) {
  return (
    <div className="flex justify-center pb-1 pt-4" role="separator">
      <span className="rounded-full bg-background/80 px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-0.5" aria-hidden="true">
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="h-1.5 w-1.5 rounded-full bg-current motion-safe:animate-bounce"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </span>
  );
}

function isAtBottom(el: HTMLElement): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/**
 * The scrolling message column of a chat. Keeps the reader at the end while
 * they are there; otherwise counts what arrived below ("↓ 2 neue
 * Nachrichten"). Older sessions load when scrolled to the top (or via the
 * button), without moving what the reader looks at.
 */
export function MessageTimeline({
  messages,
  sessions,
  channel,
  me,
  hasMore = false,
  loadingOlder = false,
  onLoadOlder,
  agentTyping = false,
  staffTyping = null,
  jump = null,
  emptyText = 'Noch keine Nachrichten',
  footer = null,
}: Props) {
  const now = useServerNow(60_000);
  const entries = useMemo(() => buildChatTimeline(messages, sessions, now), [messages, sessions, now]);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const [unseen, setUnseen] = useState(0);

  // Which message keys were already on screen: new ones at the end fade in.
  const knownKeysRef = useRef<Set<string> | null>(null);
  const messageEntries = entries.filter((e): e is Extract<TimelineEntry, { type: 'message' }> => e.type === 'message');
  const known = knownKeysRef.current;
  let lastKnownIndex = -1;
  if (known) {
    messageEntries.forEach((e, i) => {
      if (known.has(e.key)) lastKnownIndex = i;
    });
  }
  const freshKeys = new Set(
    known ? messageEntries.filter((e, i) => i > lastKnownIndex && !known.has(e.key)).map((e) => e.key) : [],
  );

  const firstKey = messageEntries[0]?.key ?? null;
  const lastKey = messageEntries[messageEntries.length - 1]?.key ?? null;
  const layoutRef = useRef<{ first: string | null; last: string | null; height: number } | null>(null);

  const scrollToEnd = useCallback((smooth = false) => {
    const el = scrollerRef.current;
    if (!el) return;
    if (smooth && !prefersReducedMotion() && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
    atBottomRef.current = true;
    setUnseen(0);
  }, []);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const prev = layoutRef.current;
    if (!prev || prev.first === null) {
      // First content: open at the most recent message.
      if (lastKey !== null) scrollToEnd();
    } else if (firstKey !== prev.first && lastKey === prev.last) {
      // Older sessions went in front: keep the visible message where it is.
      el.scrollTop += el.scrollHeight - prev.height;
    } else if (lastKey !== prev.last) {
      const added = freshKeys.size;
      const mine = messageEntries[messageEntries.length - 1]?.message;
      const ownMessage = mine?.role === 'staff' && !!me && mine.author?.toLowerCase() === me.toLowerCase();
      if (atBottomRef.current || ownMessage) scrollToEnd();
      else if (added > 0) setUnseen((n) => n + added);
    }
    layoutRef.current = { first: firstKey, last: lastKey, height: el.scrollHeight };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs per content change, reads this render's values
  }, [firstKey, lastKey, entries.length]);

  useEffect(() => {
    knownKeysRef.current = new Set(messageEntries.map((e) => e.key));
  });

  // The typing dots appear below the last message: follow them when at the end.
  useLayoutEffect(() => {
    if ((agentTyping || staffTyping) && atBottomRef.current) scrollToEnd();
  }, [agentTyping, staffTyping, scrollToEnd]);

  // Height after every commit (typing dots, header swaps …), so the next
  // prepend correction starts from what is really on screen.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el && layoutRef.current) layoutRef.current = { ...layoutRef.current, height: el.scrollHeight };
  });

  // A width change (sidebar folding, window, context column) re-wraps the
  // messages without a render: a reader at the end stays there, and the
  // prepend correction above keeps measuring from what is on screen.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollerRef.current;
    const content = contentRef.current;
    if (!el || !content || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      if (atBottomRef.current) el.scrollTop = el.scrollHeight;
      if (layoutRef.current) layoutRef.current = { ...layoutRef.current, height: el.scrollHeight };
    });
    observer.observe(el);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  // A jump is one-shot per request (nonce): it waits until its session is
  // rendered, scrolls there once, and never pulls the reader back later.
  const handledJumpRef = useRef<number | null>(null);
  useEffect(() => {
    if (!jump || handledJumpRef.current === jump.nonce) return;
    const el = scrollerRef.current;
    const target = el?.querySelector<HTMLElement>(`[data-session="${jump.conversationId}"]`);
    if (!el || !target) return;
    handledJumpRef.current = jump.nonce;
    el.scrollTop = Math.max(0, target.offsetTop - 12);
    atBottomRef.current = isAtBottom(el);
  }, [jump, entries.length]);

  // Older sessions go into the log in front: screen readers must not read
  // the whole history aloud as if it had just arrived.
  const [quiet, setQuiet] = useState(loadingOlder);
  useEffect(() => {
    if (loadingOlder) {
      setQuiet(true);
      return undefined;
    }
    const id = setTimeout(() => setQuiet(false), QUIET_AFTER_PREPEND_MS);
    return () => clearTimeout(id);
  }, [loadingOlder]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    atBottomRef.current = isAtBottom(el);
    if (atBottomRef.current && unseen > 0) setUnseen(0);
    if (el.scrollTop < LOAD_OLDER_PX && hasMore && !loadingOlder && onLoadOlder) onLoadOlder();
  };

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="relative h-full overflow-y-auto overscroll-contain bg-chrome/60 px-3 pb-4 pt-3 sm:px-6"
        style={{
          backgroundImage: 'radial-gradient(hsl(var(--foreground) / 0.045) 1px, transparent 1.2px)',
          backgroundSize: '18px 18px',
        }}
      >
        <div ref={contentRef} className="mx-auto flex max-w-3xl flex-col">
          {onLoadOlder && messages.length > 0 && (
            <div className="flex justify-center pb-2">
              {loadingOlder ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" role="status">
                  <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  Frühere Nachrichten werden geladen …
                </span>
              ) : hasMore ? (
                <button
                  type="button"
                  onClick={onLoadOlder}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  <History className="h-3.5 w-3.5" aria-hidden="true" />
                  Frühere Sitzungen laden
                </button>
              ) : (
                <span className="text-[11px] text-muted-foreground">Beginn des Verlaufs</span>
              )}
            </div>
          )}

          {messages.length === 0 ? (
            <p className="py-16 text-center text-sm text-muted-foreground">{emptyText}</p>
          ) : null}

          <div
            role="log"
            aria-live={loadingOlder || quiet ? 'off' : 'polite'}
            aria-label="Nachrichtenverlauf"
            aria-busy={loadingOlder}
            className="flex flex-col"
          >
            {entries.map((entry) => {
              if (entry.type === 'session') return <SessionSeparator key={entry.key} entry={entry} />;
              if (entry.type === 'day') return <DaySeparator key={entry.key} label={entry.label} />;
              return (
                <MessageBubble
                  key={entry.key}
                  message={entry.message}
                  channel={channel}
                  me={me}
                  groupStart={entry.groupStart}
                  groupEnd={entry.groupEnd}
                  fresh={freshKeys.has(entry.key)}
                  now={now}
                />
              );
            })}
          </div>

          {footer}

          {(agentTyping || staffTyping) && (
            <div className="mt-2 flex justify-end pl-16" role="status">
              <span className="inline-flex items-center gap-2 rounded-2xl rounded-br-md bg-brand-soft px-3 py-2 text-xs text-foreground/75 ring-1 ring-inset ring-brand/15">
                <TypingDots />
                {agentTyping ? 'Agent schreibt …' : `${staffTyping} tippt …`}
              </span>
            </div>
          )}
        </div>
      </div>

      {unseen > 0 && (
        <button
          type="button"
          onClick={() => scrollToEnd(true)}
          className="absolute bottom-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-foreground px-3.5 py-1.5 text-xs font-semibold text-background shadow-lg transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
        >
          <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
          {unseen === 1 ? '1 neue Nachricht' : `${unseen} neue Nachrichten`}
        </button>
      )}
    </div>
  );
}

export default MessageTimeline;
